<template>
  <div class="room-state">
    <div class="rs-header">
      <div class="rs-title">受控房间状态</div>
      <div class="rs-tools">
        <span class="rs-count">共 {{ rooms.length }} 个</span>
        <span class="rs-updated" v-if="lastRefresh">刷新于 {{ lastRefresh }}</span>
        <NButton size="small" quaternary :loading="loading" @click="refresh">刷新</NButton>
      </div>
    </div>

    <!-- 操作失败提示（无 n-message-provider，用行内提示） -->
    <div class="rs-op-error" v-if="opError">{{ opError }}</div>

    <!-- 统计（可点击筛选） -->
    <div class="rs-stats">
      <div class="stat-card clickable" :class="{ active: filter === 'all' }" @click="setFilter('all')">
        <div class="stat-num">{{ rooms.length }}</div>
        <div class="stat-label">总房间</div>
      </div>
      <div class="stat-card clickable" :class="{ active: filter === 'maintaining', 'has-maint': maintainingRooms.length }"
        @click="setFilter('maintaining')">
        <div class="stat-num">{{ maintainingRooms.length }}</div>
        <div class="stat-label">维护中</div>
      </div>
      <div class="stat-card clickable" :class="{ active: filter === 'disabled' }"
        @click="setFilter('disabled')">
        <div class="stat-num">{{ disabledRooms.length }}</div>
        <div class="stat-label">托管关闭</div>
      </div>
      <div class="stat-card clickable" :class="{ active: filter === 'dup', 'has-danger': duplicateRooms.length }"
        @click="setFilter('dup')">
        <div class="stat-num">{{ duplicateGroups.length }}</div>
        <div class="stat-label">主播重名组</div>
      </div>
    </div>

    <!-- 明细：谁重名了 -->
    <div class="rs-detail" v-if="duplicateGroups.length">
      <div class="detail-block">
        <div class="detail-title danger">主播重名（同一主播同时出现在多张牌台）</div>
        <div class="detail-row" v-for="g in duplicateGroups" :key="g.key">
          <span class="detail-name">{{ g.name }}</span>
          <span class="detail-chips">
            <span class="chip" v-for="room in g.rooms" :key="room.no">
              {{ room.no }}<i v-if="room.name" class="chip-name">{{ room.name }}</i>
            </span>
          </span>
        </div>
      </div>
    </div>

    <div class="rs-table">
      <div class="rs-row rs-head">
        <span class="c-room">房间</span>
        <span class="c-dealer">主播</span>
        <span class="c-status">状态</span>
        <span class="c-kill">托管</span>
        <span class="c-time" title="该房间最近一次收到上游数据包的时间，每个房间独立（开局/发牌节奏不同）">最后数据</span>
      </div>
      <div class="rs-body">
        <div class="rs-group" v-for="g in shownGroups" :key="g.key">
          <div class="rs-group-head" @click="toggleGroup(g.key)">
            <span class="g-arrow">{{ collapsed.has(g.key) ? '▸' : '▾' }}</span>
            <span class="g-name">{{ g.label }}</span>
            <span class="g-count">{{ g.rooms.length }}</span>
          </div>
          <template v-if="!collapsed.has(g.key)">
            <div class="rs-row" :class="{ 'row-dup': r.IsDuplicateDealer, 'row-maint': r.IsMaintaining }" v-for="r in g.rooms" :key="r.RoomNumber">
              <span class="c-room">
                <div class="room-key">{{ r.RoomNumber }}</div>
                <div class="room-name" v-if="r.RoomName">{{ r.RoomName }}</div>
              </span>
              <span class="c-dealer" :class="{ dup: r.IsDuplicateDealer }"
                :title="r.IsDuplicateDealer ? '主播重名：该主播同时出现在多张牌台' : undefined">
                <span class="d-name">{{ r.DealerName || '—' }}</span>
                <span class="d-id" v-if="r.DealerId" :title="'主播 ID：' + r.DealerId">{{ r.DealerId }}</span>
              </span>
              <span class="c-status">
                <span class="badge" :class="r.IsMaintaining ? 'bad-warn' : 'bad-ok'">
                  {{ r.IsMaintaining ? '维护中' : '正常' }}
                </span>
              </span>
              <span class="c-kill">
                <NSwitch size="small" :value="!!r.OpenKillPig" :loading="toggling.has(r.RoomNumber)"
                  :disabled="r.IsMaintaining" :title="r.IsMaintaining ? '维护中不可操作托管' : undefined"
                  @update:value="(v: boolean) => toggleKillPig(r, v)" />
              </span>
              <span class="c-time">{{ fmtTime(r.LastUpdateTime) }}</span>
            </div>
          </template>
        </div>
        <div class="rs-empty" v-if="!shownRooms.length && !loading">
          {{ filter === 'all' ? '暂无受控房间' : '该筛选条件下暂无房间' }}
        </div>
        <div class="rs-empty" v-if="loading && !rooms.length">加载中…</div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import SignalRClient from '@/ts/SignalRClient';
import RoomStateInfo from '@/ts/VO/RoomStateInfo';
import { NButton, NSwitch } from 'naive-ui';
import { ref, computed, onMounted, onUnmounted } from 'vue';

const rooms = ref<RoomStateInfo[]>([]);
const loading = ref(false);
const lastRefresh = ref<string>('');
let timer: number | null = null;

/** 单房间托管开关变更（成功后通知父页面同步 selectRooms 等状态） */
const emit = defineEmits<{ roomKillPigChanged: [roomNumber: string, open: boolean] }>();

/** 正在切换托管的房间（防抖，开关转圈直到接口返回） */
const toggling = ref(new Set<string>());
/** 行内错误提示（项目未挂 n-message-provider，不能用 useMessage） */
const opError = ref('');
let opErrorTimer: number | null = null;
function showOpError(msg: string) {
  opError.value = msg;
  if (opErrorTimer != null) window.clearTimeout(opErrorTimer);
  opErrorTimer = window.setTimeout(() => { opError.value = ''; }, 4000);
}

async function toggleKillPig(r: RoomStateInfo, open: boolean) {
  if (r.IsMaintaining) return; // 维护中房间不可操作托管
  if (toggling.value.has(r.RoomNumber ?? '')) return;
  toggling.value.add(r.RoomNumber ?? '');
  toggling.value = new Set(toggling.value); // 触发响应式
  try {
    const ok = await SignalRClient.Instance.InvokeAsync<boolean>('SetRoomOpenKillPig', r.GameType, r.GameCode, open);
    if (ok !== true) {
      // 服务端没落地（房间已不存在/异常被 catch）：不要乐观更新，也不要通知父页写基线
      showOpError(`切换 ${r.RoomNumber ?? ''} 托管失败：房间不存在或服务端异常`);
      await refresh();
      return;
    }
    r.OpenKillPig = open; // 乐观更新，5s 轮询会再以服务端为准
    emit('roomKillPigChanged', r.RoomNumber ?? '', open);
  } catch {
    showOpError(`切换 ${r.RoomNumber ?? ''} 托管失败：连接异常`);
    await refresh();
  } finally {
    toggling.value.delete(r.RoomNumber ?? '');
    toggling.value = new Set(toggling.value);
  }
}

/** 统计卡筛选：all / maintaining / disabled / dup */
type StatFilter = 'all' | 'maintaining' | 'disabled' | 'dup';
const filter = ref<StatFilter>('all');
function setFilter(f: StatFilter) {
  // 再点一次当前筛选 = 取消，回到全部
  filter.value = filter.value === f ? 'all' : f;
}

async function refresh() {
  loading.value = true;
  try {
    const list = await SignalRClient.Instance.InvokeAsync<RoomStateInfo[]>("GetRoomStates");
    rooms.value = list ?? [];
    lastRefresh.value = fmtNow();
  } catch {
    /* 静默失败，下次刷新重试 */
  } finally {
    loading.value = false;
  }
}

const maintainingRooms = computed(() => rooms.value.filter((r) => r.IsMaintaining));
const disabledRooms = computed(() => rooms.value.filter((r) => !r.OpenKillPig));

// 主播重名：同一房间类型(GameType，如 3001/3047)、同一系列（桌名前缀字母，如 MD/EU/AS，兼容任意前缀）内、
// 同一个主播出现在 ≥2 个受控房间。与后端 GetRoomStates / CountRoomsWithDealer 口径一致：
// 双方都有 did ⇒ 比 did（同名不同人不会误报）；任一方缺 did ⇒ 回退比 dn。
function seriesOf(roomName?: string): string {
  // 与后端 VirtualGameInfoFactoryService.GetRoomSeries 的 char.IsLetter 对齐：
  // 取开头连续 Unicode 字母（中文桌名也算前缀），统一大写。
  const m = (roomName ?? '').trim().match(/^\p{L}+/u);
  return m ? m[0].toUpperCase() : '';
}
function sameDealer(a: RoomStateInfo, b: RoomStateInfo): boolean {
  const ai = (a.DealerId ?? '').trim();
  const bi = (b.DealerId ?? '').trim();
  if (ai && bi) return ai === bi;
  const an = (a.DealerName ?? '').trim();
  const bn = (b.DealerName ?? '').trim();
  return !!an && an === bn;
}
const duplicateGroups = computed(() => {
  // 先按 房间类型+系列 分桶，再在桶内按“同一个主播”聚类（两两比较，兼容 did 有的有、有的没）
  const buckets = new Map<string, RoomStateInfo[]>();
  for (const r of rooms.value) {
    if (r.IsMaintaining) continue; // 维护中的房间荷官可能为残留，不参与重名对比
    if (!(r.DealerId ?? '').trim() && !(r.DealerName ?? '').trim()) continue;
    const key = (r.GameType ?? '') + '\u0001' + seriesOf(r.RoomName);
    if (!buckets.has(key)) buckets.set(key, []);
    buckets.get(key)!.push(r);
  }

  const groups: { key: string; name: string; rooms: { no: string; name: string }[] }[] = [];
  for (const [bucketKey, list] of buckets) {
    const clusters: RoomStateInfo[][] = [];
    for (const r of list) {
      const hit = clusters.find((c) => sameDealer(c[0], r));
      if (hit) hit.push(r);
      else clusters.push([r]);
    }
    for (const c of clusters) {
      if (c.length < 2) continue;
      const dn = (c[0].DealerName ?? '').trim();
      const did = (c[0].DealerId ?? '').trim();
      const label = dn || (did ? `ID ${did}` : '—');
      groups.push({
        key: bucketKey + '\u0001' + (did || dn),
        // 有 ID 时一并展示，便于核对“同名到底是不是同一个人”
        name: did ? `${label}（ID:${did}）` : label,
        rooms: c.map((x) => ({ no: x.RoomNumber ?? '', name: x.RoomName ?? '' })),
      });
    }
  }
  return groups;
});
const duplicateRooms = computed(() => rooms.value.filter((r) => r.IsDuplicateDealer));

const shownRooms = computed(() => {
  let list: RoomStateInfo[];
  switch (filter.value) {
    case 'maintaining': list = maintainingRooms.value; break;
    case 'disabled': list = disabledRooms.value; break;
    case 'dup': list = duplicateRooms.value; break;
    default: list = rooms.value;
  }
  return [...list].sort((a, b) => {
    // 重名房间排最前（显眼），其余按房间号
    const da = a.IsDuplicateDealer ? 1 : 0;
    const db = b.IsDuplicateDealer ? 1 : 0;
    if (da !== db) return db - da;
    return (a.RoomNumber ?? '').localeCompare(b.RoomNumber ?? '');
  });
});

/** 按房间类型(3001/3047)+房间头（桌名前缀，如 MD/EU/AS；无前缀归为“未分组”）分组；组内房间已按房间号排序 */
const shownGroups = computed(() => {
  const groups = new Map<string, { gameType: string; series: string; rooms: RoomStateInfo[] }>();
  for (const r of shownRooms.value) {
    const gt = r.GameType ?? '';
    const s = seriesOf(r.RoomName);
    const key = gt + '\u0001' + s;
    if (!groups.has(key)) groups.set(key, { gameType: gt, series: s, rooms: [] });
    groups.get(key)!.rooms.push(r);
  }
  return [...groups.values()].sort((a, b) => {
    if (a.gameType !== b.gameType) return a.gameType.localeCompare(b.gameType);
    const sa = a.series, sb = b.series;
    if (sa === '' && sb !== '') return 1;  // 未分组的排最后
    if (sb === '' && sa !== '') return -1;
    return sa.localeCompare(sb);
  }).map(g => ({
    key: g.gameType + '\u0001' + g.series,
    label: (g.series || '未分组') + (g.gameType ? ` · ${g.gameType}` : ''),
    rooms: g.rooms,
  }));
});

/** 折叠的分组（点击组头切换） */
const collapsed = ref(new Set<string>());
function toggleGroup(s: string) {
  const n = new Set(collapsed.value);
  if (n.has(s)) n.delete(s); else n.add(s);
  collapsed.value = n;
}

function pad(n: number): string {
  return n < 10 ? '0' + n : '' + n;
}
function fmtTime(iso?: string): string {
  if (!iso) return '未更新';
  const d = new Date(iso);
  if (isNaN(d.getTime()) || d.getFullYear() <= 1) return '未更新';
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(
    d.getMinutes()
  )}:${pad(d.getSeconds())}`;
}
function fmtNow(): string {
  return fmtTime(new Date().toISOString());
}

function startPoll() {
  refresh();
  timer = window.setInterval(refresh, 5000);
}
function stopPoll() {
  if (timer != null) {
    window.clearInterval(timer);
    timer = null;
  }
}

// 重连后兜底刷新一次
const onReconnect = () => refresh();

onMounted(() => {
  startPoll();
  SignalRClient.Instance.AddReconnectListener(onReconnect);
});
onUnmounted(() => {
  stopPoll();
  if (opErrorTimer != null) {
    window.clearTimeout(opErrorTimer);
    opErrorTimer = null;
  }
  SignalRClient.Instance.RemoveReconnectListener(onReconnect);
});
</script>

<style scoped lang="scss">
.room-state {
  // 统一语义色（与下方表格面板一致）
  --kp-gold: #d8b25f;
  --kp-gold-bright: #f6e3b6;
  --kp-red: #e0584a;     // 重名（冲突/异常）
  --kp-green: #7ec9a4;   // 正常
  --kp-gray: #9a9080;    // 维护中（不可用，灰扑扑）
  // 面板底色：浅金半透明，在深色纹理页面上能“浮”出边界（不要用实色深棕，会和页面底融为一体）
  --kp-panel: linear-gradient(160deg, rgba(255, 240, 210, .07), rgba(255, 240, 210, .02));

  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
  min-height: 0;
}

.rs-op-error {
  padding: 8px 12px;
  border: 1px solid rgba(224, 88, 74, .45);
  border-radius: 7px;
  background: rgba(224, 88, 74, .10);
  color: var(--kp-red);
  font-size: 12px;
}

.rs-header {
  display: flex;
  justify-content: space-between;
  align-items: center;

  .rs-title {
    font-size: 16px;
    font-weight: 700;
    letter-spacing: 2px;
    // 标题只在行内被挤压时也绝不断成两行（手机版另有 @media 直接隐藏）
    white-space: nowrap;
    flex-shrink: 0;
    background-image: linear-gradient(180deg, #fdf3da, #d9b773 60%, #b3893f);
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
    filter: drop-shadow(0 1px 0 rgba(0, 0, 0, .6));
  }

  .rs-tools {
    display: flex;
    align-items: center;
    gap: 10px;

    .rs-count {
      font-size: 12px;
      color: #9b8f79;
    }

    .rs-updated {
      font-size: 11px;
      color: #7e8a76;
      font-family: var(--kp-mono);
    }
  }
}

// ---------- 统计卡（可点击筛选） ----------
.rs-stats {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;
}

.stat-card {
  background-image: var(--kp-panel);
  border: 1px solid rgba(216, 178, 95, .20);
  border-radius: 10px;
  padding: 12px 10px;
  text-align: center;
  cursor: pointer;
  user-select: none;
  transition: border-color .15s, background .15s, transform .1s;

  &:hover {
    border-color: rgba(216, 178, 95, .42);
  }

  &:active {
    transform: scale(.98);
  }

  .stat-num {
    font-size: 22px;
    font-weight: 800;
    color: var(--kp-gold-bright);
    font-family: var(--kp-mono);
    line-height: 1.1;
  }

  .stat-label {
    margin-top: 4px;
    font-size: 11px;
    color: #9b8f79;
  }

  // 维护中：灰扑扑（不可用，不抢眼）
  &.has-maint {
    border-color: rgba(154, 144, 128, .35);
    background-image: linear-gradient(160deg, rgba(154, 144, 128, .10), rgba(154, 144, 128, .03));

    .stat-num {
      color: var(--kp-gray);
    }
  }

  // 重名：红
  &.has-danger {
    border-color: rgba(224, 88, 74, .55);
    background-image: linear-gradient(160deg, rgba(224, 88, 74, .16), rgba(224, 88, 74, .04));

    .stat-num {
      color: var(--kp-red);
    }
  }

  // 当前选中的筛选
  &.active {
    border-color: rgba(240, 208, 138, .8);
    background-image: linear-gradient(160deg, rgba(216, 178, 95, .22), rgba(216, 178, 95, .06));
    box-shadow: 0 0 0 1px rgba(240, 208, 138, .35) inset;

    .stat-label {
      color: #f6e3b6;
    }
  }
}

// ---------- 明细（重名主播） ----------
.rs-detail {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.detail-block {
  border: 1px solid rgba(216, 178, 95, .14);
  border-radius: 10px;
  padding: 10px 12px;
  background: rgba(255, 240, 210, .03);

  .detail-title {
    font-size: 12px;
    font-weight: 600;
    margin-bottom: 6px;

    &.danger {
      color: var(--kp-red);
    }
  }

  .detail-row {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    font-size: 12px;
    padding: 4px 0;

    .detail-name {
      color: #efe6d4;
      font-weight: 600;
      flex: 0 0 auto;
      line-height: 22px;
    }
  }
}

.detail-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.chip {
  display: inline-block;
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 6px;
  color: #cbbda2;
  background: rgba(216, 178, 95, .10);
  border: 1px solid rgba(216, 178, 95, .18);
  font-family: var(--kp-mono);
  line-height: 18px;

  .chip-name {
    font-style: normal;
    font-family: inherit;
    margin-left: 5px;
    padding-left: 6px;
    border-left: 1px solid rgba(216, 178, 95, .25);
    color: #efe6d4;
  }
}

.rs-table {
  border: 1px solid rgba(216, 178, 95, .14);
  border-radius: 12px;
  overflow: hidden;
  background-image: var(--kp-panel);
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.rs-row {
  display: grid;
  grid-template-columns: 1.4fr 1.3fr 0.9fr 0.8fr 1.5fr;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;

  &.rs-head {
    font-size: 11px;
    color: #9b8f79;
    letter-spacing: .6px;
    border-bottom: 1px solid rgba(216, 178, 95, .18);
    background: rgba(216, 178, 95, .08);
  }

  // 维护中房间：灰扑扑不可用，不抢眼（淡灰底 + 灰左条，明显弱于重名红）
  &.row-maint {
    background-image: linear-gradient(90deg, rgba(154, 144, 128, .16), rgba(154, 144, 128, .03));
    box-shadow: inset 3px 0 0 0 rgba(154, 144, 128, .7);
  }

  // 重名房间整行高亮（红色渐变 + 左侧红条）；若同时维护，红色优先
  &.row-dup {
    background-image: linear-gradient(90deg, rgba(224, 88, 74, .18), rgba(224, 88, 74, .04));
    box-shadow: inset 3px 0 0 0 rgba(224, 88, 74, .85);
  }
}

.rs-body {
  overflow-y: auto;
  flex: 1;
  min-height: 0;

  .rs-group + .rs-group {
    border-top: 1px solid rgba(216, 178, 95, .08);
  }

  .rs-group-head {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 7px 14px;
    cursor: pointer;
    user-select: none;
    // 实底不透明（吸顶时不能透出行内容），但比页面底亮一档以便区分
    background-image: linear-gradient(180deg, #3d3420, #2e2716);
    border-bottom: 1px solid rgba(216, 178, 95, .35);
    position: sticky;
    top: 0;
    z-index: 1;

    .g-arrow {
      font-size: 10px;
      color: #cbbda2;
    }

    .g-name {
      font-size: 13px;
      font-weight: 800;
      letter-spacing: 1.5px;
      color: #f6e3b6;
      font-family: var(--kp-mono);
      text-shadow: 0 1px 0 rgba(0, 0, 0, .6);
    }

    .g-count {
      font-size: 11px;
      color: #d8c9a8;
      font-family: var(--kp-mono);
      padding: 1px 8px;
      border-radius: 999px;
      background: rgba(0, 0, 0, .35);
      border: 1px solid rgba(216, 178, 95, .22);
    }

    &:hover {
      background-image: linear-gradient(180deg, #473c25, #352c1a);
    }
  }

  .rs-row:not(.rs-head) {
    border-bottom: 1px solid rgba(216, 178, 95, .08);

    &:last-child {
      border-bottom: none;
    }
  }

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(216, 178, 95, .22);
    border-radius: 2px;
  }
}

.c-room {
  display: flex;
  flex-direction: column;
  gap: 2px;

  .room-key {
    font-size: 13px;
    font-weight: 700;
    color: #efe6d4;
    font-family: var(--kp-mono);
  }

  .room-name {
    font-size: 11px;
    color: #9b8f79;
  }
}

.c-dealer {
  font-size: 13px;
  color: #cbbda2;
  // 名字 + ID 自适应换行：列宽不够时 ID 掉到第二行，不挤压名字也不截断 ID
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  column-gap: 6px;

  // 主播 ID（did）：小号弱化，便于核对「同名到底是不是同一个人」
  .d-id {
    font-size: 11px;
    font-weight: 600;
    color: #8a7f6d;
    font-family: var(--kp-mono);
    white-space: nowrap;
  }

  // 主播重名：直接用颜色高亮，代替原来的「重名」徽标列
  &.dup {
    color: var(--kp-red);
    font-weight: 800;
    text-shadow: 0 0 10px rgba(224, 88, 74, .5);
  }
}

.c-time {
  font-size: 12px;
  color: #9b8f79;
  font-family: var(--kp-mono);
}

.badge {
  display: inline-block;
  font-size: 11px;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 999px;
  border: 1px solid transparent;

  &.bad-ok {
    color: #bff0d8;
    background: linear-gradient(180deg, rgba(126, 201, 164, .26), rgba(126, 201, 164, .10));
    border-color: rgba(126, 201, 164, .55);
  }

  &.bad-warn {
    color: #b3ab9b;
    background: linear-gradient(180deg, rgba(154, 144, 128, .16), rgba(154, 144, 128, .06));
    border-color: rgba(154, 144, 128, .35);
  }

  &.bad-danger {
    color: #ffb3a8;
    background: linear-gradient(180deg, rgba(224, 88, 74, .24), rgba(224, 88, 74, .10));
    border-color: rgba(224, 88, 74, .55);
  }

  &.bad-dim {
    color: #6d6454;
    background: rgba(255, 240, 210, .04);
    border-color: rgba(216, 178, 95, .14);
  }
}

.rs-empty {
  padding: 28px 0;
  text-align: center;
  font-size: 12px;
  color: #6d6454;
}

// ---------- 手机版：顶部不再串行 ----------
// 标题（受控房间状态）+ 共 N 个 + 刷新时间 + 按钮 挤在一行时，标题会被压成两行（"受控房间状/态"）。
// 手机上去掉标题（底部导航已经是「房间」），其余三项压成一行：数字靠左、时间靠右、末尾按钮。
@media (max-width: 640px) {
  .rs-header {
    gap: 8px;

    .rs-title {
      display: none;
    }

    .rs-tools {
      flex: 1;
      min-width: 0;
      gap: 8px;

      .rs-count {
        flex: 0 0 auto;
        font-size: 11px;
      }

      // 时间允许被压缩成省略号，绝不换行
      .rs-updated {
        flex: 1 1 auto;
        min-width: 0;
        text-align: right;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        font-size: 10px;
      }
    }
  }
}
</style>
